export { COOKIE_NAME, ONE_YEAR_MS } from "@shared/const";

/**
 * Brand identity — temporary name, subject to change. Import this instead
 * of hardcoding the name/slogan in individual pages/components, so a
 * future rename touches one place.
 */
export const BRAND_NAME = "FRAME O";
export const BRAND_SLOGAN = "사진 한 장, 작품 한 점.";

/** Must match server/_core/googleOAuth.ts's STATE_COOKIE_NAME. */
const GOOGLE_OAUTH_STATE_COOKIE = "google_oauth_state";

/**
 * Where to send the visitor once they're back from Google login — read by
 * consumePostLoginRedirect() below. This is a client-only convenience on
 * top of the OAuth flow, not part of it: googleOAuth.ts's callback still
 * always redirects to "/" exactly as before (unchanged, per this round's
 * "don't touch Google auth core" constraint); this just remembers, in
 * sessionStorage, what the visitor was trying to reach, and forwards them
 * there once they land back on "/" already logged in.
 */
const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

/**
 * Login entry point — Google OAuth (Authorization Code flow). Generates
 * the Google authorization URL at runtime so redirect_uri reflects the
 * current origin. Manus OAuth has been fully removed from this app.
 *
 * returnPath is remembered (see POST_LOGIN_REDIRECT_KEY) so
 * consumePostLoginRedirect() can send the visitor onward after login,
 * since the OAuth callback itself always lands on "/".
 */
// Production build refresh for Railway Vite environment
export const getLoginUrl = (returnPath?: string) => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Google OAuth isn't configured in this environment (e.g. local dev
  // before GOOGLE_CLIENT_ID/SECRET/REDIRECT_URI are set). Several callers
  // (Home.tsx, useAuth's default redirectPath) invoke this unconditionally
  // on every render, so throwing here crashes the whole page. Degrade to a
  // no-op link instead; once configured, behavior below is unchanged.
  if (!clientId) {
    return "#";
  }

  if (returnPath && returnPath !== "/") {
    try {
      window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, returnPath);
    } catch {
      // Private-browsing/storage-disabled edge case — login still works,
      // the visitor just lands on "/" instead of returnPath afterward.
    }
  }

  const redirectUri = `${window.location.origin}/api/oauth/google/callback`;

  // CSRF defense: a random, unguessable value that must appear both in the
  // authorization URL's state param AND in this cookie. The callback route
  // rejects the request unless they match (double-submit-cookie pattern) —
  // an attacker forging a callback request has no way to know/set the
  // victim's cookie value. Re-set on every call so the value the user
  // actually clicks through with always matches what's currently in the
  // cookie, even across re-renders.
  const state = crypto.randomUUID();
  const isHttps = window.location.protocol === "https:";
  document.cookie = `${GOOGLE_OAUTH_STATE_COOKIE}=${state}; path=/; max-age=600; samesite=lax${isHttps ? "; secure" : ""}`;

  const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("state", state);

  return url.toString();
};

/**
 * Call once, from wherever the OAuth callback lands (currently always "/"
 * — see Home.tsx). Returns the path getLoginUrl() remembered before
 * redirecting to Google, if any, and clears it so it's only used once.
 */
export const consumePostLoginRedirect = (): string | null => {
  try {
    const path = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (path) window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return path;
  } catch {
    return null;
  }
};
